/**
 * Status Timeline Card Component
 * Displays dispute task logs timeline
 */

import React from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  CheckCircle as CompletedIcon,
  EmojiEvents as PendingIcon,
  Autorenew as InProgressIcon,
  Close as CanceledIcon,
} from '@mui/icons-material';
import type { DisputeLogEntry } from '../../../../types/dispute';
import { formatDateTime } from '../../../../utils/dateUtils';

interface StatusTimelineCardProps {
  statusHistory: DisputeLogEntry[];
}

const getStatusConfig = (status: string, theme: any) => {
  // Map status titles to icon configs (same logic as Mission StatusTimelineCard)
  // Mission uses default grey icon for most cases, so we'll match that behavior
  switch (status) {
    case 'Completed':
      return {
        icon: <CompletedIcon sx={{ fontSize: 16 }} />,
        backgroundColor: theme.palette.success.main,
        iconColor: theme.palette.common.white,
      };
    case 'Cancelled':
    case 'Canceled':
      return {
        icon: <CanceledIcon sx={{ fontSize: 16 }} />,
        backgroundColor: theme.palette.error.main,
        iconColor: theme.palette.common.white,
      };
    case 'Mission In Progress':
      return {
        icon: <InProgressIcon sx={{ fontSize: 16 }} />,
        backgroundColor: theme.palette.info.main,
        iconColor: theme.palette.common.white,
      };
    case 'Pending':
      return {
        icon: <PendingIcon sx={{ fontSize: 16 }} />,
        backgroundColor: theme.palette.warning.main,
        iconColor: theme.palette.common.white,
      };
    default:
      // Default: grey icon (same as Mission StatusTimelineCard)
      return {
        icon: <PendingIcon sx={{ fontSize: 16 }} />,
        backgroundColor: theme.palette.grey[600],
        iconColor: theme.palette.common.white,
      };
  }
};

export const StatusTimelineCard: React.FC<StatusTimelineCardProps> = ({ statusHistory }) => {
  const theme = useTheme();

  if (!statusHistory || statusHistory.length === 0) {
    return (
      <Card>
        <CardContent>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 600,
              marginBottom: theme.spacing(2),
              color: theme.palette.text.primary,
            }}
          >
            Status Timeline
          </Typography>
          <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
            No status history available.
          </Typography>
        </CardContent>
      </Card>
    );
  }

  // Sort by timestamp descending (newest first)
  const sortedHistory = [...statusHistory].sort(
    (a, b) => b.timestamp.getTime() - a.timestamp.getTime()
  );

  return (
    <Card>
      <CardContent>
        <Typography
          variant="h6"
          sx={{
            fontWeight: 600,
            marginBottom: theme.spacing(2.5),
            color: theme.palette.text.primary,
            fontSize: '1rem',
          }}
        >
          Status Timeline
        </Typography>

        <Box sx={{ position: 'relative', paddingLeft: theme.spacing(3.5) }}>
          {/* Timeline line */}
          <Box
            sx={{
              position: 'absolute',
              left: theme.spacing(1.75),
              top: 0,
              bottom: 0,
              width: 2,
              backgroundColor: theme.customColors.tableBorder,
            }}
          />

          {sortedHistory.map((item, index) => {
            const config = getStatusConfig(item.status, theme);
            const formattedDateTime = formatDateTime(item.timestamp);

            return (
              <Box
                key={index}
                sx={{
                  position: 'relative',
                  marginBottom: index < sortedHistory.length - 1 ? theme.spacing(3) : 0,
                }}
              >
                {/* Timeline dot */}
                <Box
                  sx={{
                    position: 'absolute',
                    left: theme.spacing(-2.75),
                    top: theme.spacing(0.25),
                    width: 20,
                    height: 20,
                    borderRadius: '50%',
                    backgroundColor: config.backgroundColor,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: config.iconColor,
                    zIndex: 1,
                  }}
                >
                  {config.icon}
                </Box>

                {/* Content */}
                <Box>
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 500,
                      color: theme.palette.text.primary,
                      marginBottom: theme.spacing(0.5),
                      fontSize: '0.875rem',
                      lineHeight: 1.5,
                    }}
                  >
                    {item.status}
                    {item.description && `: ${item.description}`}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: theme.spacing(0.75), alignItems: 'baseline' }}>
                    <Typography
                      variant="caption"
                      sx={{
                        color: theme.palette.text.secondary,
                        fontSize: '0.75rem',
                      }}
                    >
                      {formattedDateTime}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            );
          })}
        </Box>
      </CardContent>
    </Card>
  );
};
